import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

// GOAT SPAM Protector — the Clean My Number modal (#16169).
//
// One step per registration site, in the guide's order, then a summary. A
// step whose `data-done-on` is set was already submitted for this number's
// current flag, so opening the modal resumes at the first unfinished site.
//
// The sites refuse to be framed (X-Frame-Options), so "Open <site>" opens the
// real site in its own window on the right half of the screen, beside GOAT; on
// a narrow viewport it opens an ordinary new tab. A blocked pop-up shows the
// step's inline hint with a plain link, never a native alert.
//
// "I Submitted It, Next" records the site through the fix_steps endpoint and
// moves to the next unfinished site; closing the modal after a submission
// reloads the page so the card shows the new submission.
export const NARROW_VIEWPORT_MAX = 768
const GENERIC_ERROR = "We could not save that. Please try again."

export default class extends Controller {
  static targets = [
    "step", "summary", "segment", "summaryTitle", "summaryItem", "error",
    "closeButton", "backButton", "skipButton", "nextButton", "doneButton"
  ]

  static values = { url: String, anchor: String }

  connect() {
    this.changed = false
    this.show(this.firstUnfinished())
  }

  open() {
    this.hideError()
    this.show(this.firstUnfinished())
  }

  closed() {
    if (!this.changed) return

    this.changed = false
    this.reloadPage()
  }

  back() {
    if (this.index > 0) this.show(this.index - 1)
  }

  skip() {
    this.show(this.nextUnfinished(this.index))
  }

  async submit(event) {
    const step = this.stepTargets[this.index]
    if (!step) return

    const button = event?.currentTarget || this.nextButtonTarget
    this.setBusy(button, true)
    this.hideError()
    try {
      const response = await post(this.urlValue, {
        body: JSON.stringify({ site: step.dataset.site }),
        contentType: "application/json",
        responseKind: "json"
      })
      const data = await response.json
      if (!response.ok) {
        this.showError(data?.error || GENERIC_ERROR)
        return
      }
      step.dataset.doneOn = data.done_on
      this.changed = true
      this.show(this.nextUnfinished(this.index))
    } catch (_error) {
      this.showError(GENERIC_ERROR)
    } finally {
      this.setBusy(button, false)
    }
  }

  openSite(event) {
    event.preventDefault()
    const link = event.currentTarget
    const step = link.closest("[data-spam-protector-clean-target='step']")
    const site = step?.dataset.site || "site"
    const win = window.innerWidth <= NARROW_VIEWPORT_MAX
      ? window.open(link.href, "_blank")
      : window.open(link.href, `gl-spam-fix-${site}`, this.besideFeatures())

    const blocked = !win || win.closed
    if (!blocked) {
      // The sites never need a handle back to GOAT (the noopener we can apply
      // without losing the blocked-pop-up check, which noopener in the feature
      // string would hide by always returning null).
      try { win.opener = null } catch (_error) { /* cross-origin: nothing to clear */ }
    }
    const hint = step?.querySelector("[data-spam-protector-clean-blocked]")
    if (hint) hint.hidden = !blocked
  }

  // The right half of the screen, full height, so the site sits beside GOAT.
  besideFeatures() {
    const screen = window.screen || {}
    const availWidth = screen.availWidth || window.innerWidth
    const availHeight = screen.availHeight || window.innerHeight
    const width = Math.round(availWidth / 2)
    const left = (screen.availLeft || 0) + availWidth - width
    const top = screen.availTop || 0
    return `popup,width=${width},height=${availHeight},left=${left},top=${top}`
  }

  // ---- state ----------------------------------------------------------

  get total() {
    return this.stepTargets.length
  }

  isDone(step) {
    return Boolean(step.dataset.doneOn)
  }

  firstUnfinished() {
    const index = this.stepTargets.findIndex((step) => !this.isDone(step))
    return index === -1 ? this.total : index
  }

  nextUnfinished(from) {
    const index = this.stepTargets.findIndex((step, i) => i > from && !this.isDone(step))
    return index === -1 ? this.total : index
  }

  show(index) {
    this.index = index
    const onSummary = index >= this.total
    const middle = index > 0 && !onSummary

    this.stepTargets.forEach((step, i) => { step.hidden = i !== index })
    if (this.hasSummaryTarget) this.summaryTarget.hidden = !onSummary
    this.segmentTargets.forEach((segment, i) => {
      const step = this.stepTargets[i]
      const current = i === index
      segment.classList.toggle("gl-stepper-compact__segment--current", current)
      segment.classList.toggle("gl-stepper-compact__segment--done", !current && step && this.isDone(step))
    })

    this.toggle("closeButton", index !== 0)
    this.toggle("backButton", !middle)
    this.toggle("skipButton", !middle)
    this.toggle("nextButton", onSummary)
    this.toggle("doneButton", !onSummary)
    if (onSummary) this.renderSummary()
  }

  renderSummary() {
    const done = this.stepTargets.filter((step) => this.isDone(step)).length
    if (this.hasSummaryTitleTarget) {
      this.summaryTitleTarget.textContent = done === this.total
        ? `All ${this.total} Sites Registered`
        : `${done} of ${this.total} Sites Registered`
    }
    this.summaryItemTargets.forEach((item) => {
      const step = this.stepTargets.find((s) => s.dataset.site === item.dataset.site)
      const date = item.querySelector("[data-spam-protector-clean-date]")
      if (step && date) date.textContent = step.dataset.doneOn || "Not submitted yet"
    })
  }

  toggle(name, hidden) {
    const key = `has${name[0].toUpperCase()}${name.slice(1)}Target`
    if (this[key]) this[`${name}Target`].hidden = hidden
  }

  setBusy(button, busy) {
    if (!button) return

    button.disabled = busy
    button.setAttribute("aria-busy", busy ? "true" : "false")
    const spinner = button.querySelector(".gl-btn-spinner")
    if (busy && !spinner) {
      const span = document.createElement("span")
      span.className = "gl-btn-spinner"
      span.setAttribute("aria-hidden", "true")
      button.prepend(span)
    } else if (!busy && spinner) {
      spinner.remove()
    }
  }

  showError(message) {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
  }

  hideError() {
    if (this.hasErrorTarget) this.errorTarget.hidden = true
  }

  reloadPage() {
    const url = `${window.location.pathname}${window.location.search}${this.hasAnchorValue ? `#${this.anchorValue}` : ""}`
    if (window.Turbo) {
      window.Turbo.visit(url, { action: "replace" })
    } else {
      window.location.reload()
    }
  }
}
